(function (global) {
// Kundedialogadapter som lastes før backend-fasaden.
// En kortvarig design-fallback kunne lagre disse fem eksakte fixture-linjene i en
// kundedialog dersom foretaksstate var null under lasting. Behold en linje dersom
// produkt-id-en faktisk finnes i foretakets register; ellers fjernes bare den eksakte
// id/navn/pris-signaturen fra designet. Dette unngaar aa slette legitime fritekstlinjer.
const LEGACY_DESIGN_QUOTE_LINE_SIGNATURES = new Set([
  'p1|konsulenttime|1150',
  'p2|webdesign-pakke|24000',
  'p3|manedlig support|2900',
  'p4|workshop (halvdag)|9500',
  'p5|usb-mikrofon|1490',
]);
const legacyDesignQuoteLineName = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('nb-NO')
  .trim();
const isLegacyDesignQuoteLine = (line, registeredProductIds = new Set()) => {
  const productId = String(line?.productId || '').trim();
  if (!productId || registeredProductIds.has(productId)) return false;
  const name = legacyDesignQuoteLineName(line?.productName || line?.name || line?.description);
  return LEGACY_DESIGN_QUOTE_LINE_SIGNATURES.has(`${productId}|${name}|${num(line?.unitPrice ?? line?.price)}`);
};

const normalizeCustomerConversationThread = (thread = {}) => {
  const createdAt = thread.createdAt || nowIso();
  const status = ['drafting', 'waiting_customer', 'customer_waiting', 'ready_for_offer', 'offer_sent', 'closed'].includes(thread.status)
    ? thread.status
    : 'drafting';
  const registeredProductIds = new Set(safeArray(runtime.appState?.products).map((product) => String(product?.id || '').trim()).filter(Boolean));
  const quoteLines = safeArray(thread.quoteLines).filter((line) => !isLegacyDesignQuoteLine(line, registeredProductIds)).map((line, index) => {
    const vatRate = num(line.vatRate ?? line.vat, standardVatForCountry());
    return stripUndefinedDeep({
      id: String(line.id || line.productId || `quote-${index}`),
      productId: line.productId || line.id || '',
      productName: line.productName || line.name || line.description || '',
      description: line.description || line.productName || line.name || 'Uten beskrivelse',
      quantity: Math.max(1, num(line.quantity ?? line.qty, 1)),
      unitPrice: num(line.unitPrice ?? line.price),
      discountValue: num(line.discountValue ?? line.disc),
      discountType: line.discountType || 'percent',
      vatRate,
      vatTreatment: line.vatTreatment || (vatRate === 0 ? 'zeroRated' : 'standard'),
      salesVatCode: line.salesVatCode || undefined,
      salesAccountCode: line.salesAccountCode || '3000',
    });
  });
  return stripUndefinedDeep({
    ...thread,
    id: String(thread.id || createId('conversation')),
    createdAt,
    updatedAt: thread.updatedAt || nowIso(),
    title: String(thread.title || 'Kundedialog').trim() || 'Kundedialog',
    status: quoteLines.length > 0 && status === 'drafting' ? 'ready_for_offer' : status,
    messages: safeArray(thread.messages)
      .map((message) => ({
        id: String(message.id || createId('message')),
        createdAt: message.createdAt || nowIso(),
        direction: ['outbound', 'inbound', 'internal'].includes(message.direction) ? message.direction : 'outbound',
        text: String(message.text || '').slice(0, 10000),
      }))
      .filter((message) => message.text.trim()),
    quoteLines,
  });
};

const createCustomerConversationThread = (title = 'Kundedialog') =>
  normalizeCustomerConversationThread({
    id: createId('conversation'),
    createdAt: nowIso(),
    updatedAt: nowIso(),
    title,
    status: 'drafting',
    messages: [],
    quoteLines: [],
  });

const replaceCustomerLocal = (nextCustomer) => {
  const state = mergeState(runtime.appState);
  runtime.appState = mergeState({
    ...state,
    customers: safeArray(state.customers).map((customer) => customer.id === nextCustomer.id ? nextCustomer : customer),
  });
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.customers));
};

// ---------------- Kundedialoger ----------------
// Traadene laa tidligere i `customer.conversationThreads` inne i app/state. Siden hele
// app-state er ett Firestore-dokument, betydde en enkelt chatmelding at hele regnskapet
// — fakturaer, utgifter, bilag, produkter — ble dypkopiert, serialisert og lastet opp.
// Kostnaden vokste med datamengden, og etter en import ble den dominerende.
//
// Naa er en traad ett dokument under businesses/{id}/customers/{customerId}/conversations.
// App-state beholder bare et lite sammendrag, og det skrives kun naar sammendraget
// faktisk endrer seg — ikke ved hver melding.

const conversationsCollection = (customerId) => runtime.db
  .collection('businesses').doc(runtime.activeBusinessId)
  .collection('customers').doc(customerId)
  .collection('conversations');

const conversationCloudReady = () => Boolean(runtime.db && runtime.activeBusinessId && runtime.auth?.currentUser);

const sortConversationThreads = (threads) => safeArray(threads)
  .slice()
  .sort((a, b) => String(b?.updatedAt || '').localeCompare(String(a?.updatedAt || '')));

// Det kundelisten og dashbordet trenger for aa telle oppfolging, uten aa lese traadene.
const buildConversationSummary = (threads) => {
  const list = safeArray(threads);
  return {
    count: list.length,
    waiting: list.some((thread) => thread?.status === 'customer_waiting'),
    readyForOffer: list.some((thread) => thread?.status === 'ready_for_offer'),
    updatedAt: sortConversationThreads(list)[0]?.updatedAt || '',
  };
};

const sameConversationSummary = (a, b) => Boolean(a) === Boolean(b)
  && (a?.count || 0) === (b?.count || 0)
  && Boolean(a?.waiting) === Boolean(b?.waiting)
  && Boolean(a?.readyForOffer) === Boolean(b?.readyForOffer);

// Skriver sammendraget inn i app-state, men bare naar det faktisk er endret. Uten denne
// sjekken ville vi vaert tilbake til en full state-skriving per melding.
const syncConversationSummary = async (customerId, threads) => {
  const summary = buildConversationSummary(threads);
  const current = safeArray(mergeState(runtime.appState).customers).find((entry) => entry.id === customerId);
  if (!current || sameConversationSummary(current.conversationSummary, summary)) return;
  await updateStateAndPersist((state) => {
    const customers = safeArray(state.customers);
    const index = customers.findIndex((entry) => entry.id === customerId);
    if (index < 0) return state;
    const nextCustomers = [...customers];
    nextCustomers[index] = stripUndefinedDeep({
      ...customers[index],
      conversationSummary: summary,
      nextFollowUpDate: summary.waiting ? todayIso() : customers[index].nextFollowUpDate,
      updatedAt: nowIso(),
    });
    return { ...state, customers: nextCustomers };
  });
};

// Lat migrering: forste gang en kundes dialoger leses etter denne endringen, flyttes de
// ut av app-state. Gjores per kunde ved behov i stedet for som en engangsjobb, slik at
// ingen trenger nedetid og en feilende migrering bare rammer en kunde.
// Toemmer legacy-feltet. Kalles forst naar traadene er bekreftet skrevet til
// subcollectionen. Er den gamle lista allerede tom, skrives ingenting.
const clearLegacyConversationThreads = async (customerId, threads) => {
  const current = safeArray(mergeState(runtime.appState).customers).find((entry) => entry.id === customerId);
  if (!safeArray(current?.conversationThreads).length) return;
  await updateStateAndPersist((state) => {
    const customers = safeArray(state.customers);
    const index = customers.findIndex((entry) => entry.id === customerId);
    if (index < 0) return state;
    const nextCustomers = [...customers];
    nextCustomers[index] = stripUndefinedDeep({
      ...customers[index],
      conversationThreads: [],
      conversationSummary: buildConversationSummary(threads),
    });
    return { ...state, customers: nextCustomers };
  });
};

const migrateConversationThreadsToCloud = async (customerId, legacyThreads) => {
  const threads = safeArray(legacyThreads).map(normalizeCustomerConversationThread);
  if (!threads.length) return threads;
  const batch = runtime.db.batch();
  threads.forEach((thread) => {
    batch.set(conversationsCollection(customerId).doc(thread.id), stripUndefinedDeep({
      ...thread,
      updatedByUserId: runtime.auth.currentUser.uid,
      updatedBySource: 'client',
      migratedAt: nowIso(),
    }));
  });
  await batch.commit();
  await clearLegacyConversationThreads(customerId, threads);
  return threads;
};

const persistCleanedDesignFixtureLines = async (rows) => {
  const affected = safeArray(rows).filter((row) =>
    safeArray(row?.raw?.quoteLines).length > safeArray(row?.normalized?.quoteLines).length
  );
  if (!affected.length) return 0;
  const cleanedAt = nowIso();
  const batch = runtime.db.batch();
  affected.forEach(({ ref, normalized }) => {
    batch.set(ref, stripUndefinedDeep({
      quoteLines: normalized.quoteLines,
      updatedAt: cleanedAt,
      updatedByUserId: runtime.auth.currentUser.uid,
      updatedBySource: 'client-design-fixture-cleanup',
      designFixtureCleanupAt: cleanedAt,
    }), { merge: true });
  });
  await batch.commit();
  return affected.length;
};

// Leser fra subcollectionen, men antar aldri at legacy-feltet er tomt: backend-flytene
// (innkommende kunde-epost, tilbudsaksept) skriver fortsatt til `conversationThreads` i
// app-state. Derfor flettes begge kilder ved hver lasting, og alt som bare finnes i
// legacy — eller er nyere der — migreres. Da kan en backend-skriving aldri gaa tapt i
// vinduet mellom kopiering og opprydding, og migreringen kan kjores om igjen trygt.
const loadCustomerConversationThreads = async (customerId) => {
  if (!customerId) throw new Error(t('svc.missingCustomer'));
  const customer = safeArray(mergeState(runtime.appState).customers).find((entry) => entry.id === customerId);
  const legacyThreads = safeArray(customer?.conversationThreads).map(normalizeCustomerConversationThread);
  if (!conversationCloudReady()) return sortConversationThreads(legacyThreads);

  const snapshot = await conversationsCollection(customerId).get();
  const cloudRows = snapshot.docs.map((doc) => {
    const raw = { id: doc.id, ...doc.data() };
    return { ref: doc.ref, raw, normalized: normalizeCustomerConversationThread(raw) };
  });
  const cloudThreads = cloudRows.map((row) => row.normalized);
  // Lesemodellen er ren uansett, men forsok ogsa aa fjerne de eksakte designlinjene
  // permanent. En avvist/offline skriving skal ikke gjore dialogen utilgjengelig;
  // neste lasting prover oppryddingen igjen.
  try {
    await persistCleanedDesignFixtureLines(cloudRows);
  } catch (error) {
    console.warn('[PlummeBackend] Klarte ikke aa lagre opprydding av designprodukter.', error);
  }
  const cloudById = new Map(cloudThreads.map((thread) => [thread.id, thread]));

  const needsMigration = legacyThreads.filter((thread) => {
    const existing = cloudById.get(thread.id);
    return !existing || String(thread.updatedAt || '') > String(existing.updatedAt || '');
  });
  if (!legacyThreads.length) return sortConversationThreads(cloudThreads);
  if (needsMigration.length) await migrateConversationThreadsToCloud(customerId, needsMigration);
  else await clearLegacyConversationThreads(customerId, cloudThreads);

  needsMigration.forEach((thread) => cloudById.set(thread.id, thread));
  return sortConversationThreads([...cloudById.values()]);
};

// Brukes naar to kunder slaas sammen. Returnerer null uten sky, slik at kalleren vet
// at legacy-feltet fortsatt er sannheten.
const moveCustomerConversationThreads = async (fromCustomerId, toCustomerId) => {
  if (!conversationCloudReady()) return null;
  const [fromThreads, toThreads] = await Promise.all([
    loadCustomerConversationThreads(fromCustomerId),
    loadCustomerConversationThreads(toCustomerId),
  ]);
  if (!fromThreads.length) return toThreads;
  const batch = runtime.db.batch();
  fromThreads.forEach((thread) => {
    batch.set(conversationsCollection(toCustomerId).doc(thread.id), stripUndefinedDeep({
      ...thread,
      updatedByUserId: runtime.auth.currentUser.uid,
      updatedBySource: 'client',
    }));
    batch.delete(conversationsCollection(fromCustomerId).doc(thread.id));
  });
  await batch.commit();
  return sortConversationThreads([...toThreads, ...fromThreads]);
};

const saveCustomerConversationThread = async (customerId, thread) => {
  if (!customerId) throw new Error(t('svc.missingCustomer'));
  const normalizedThread = normalizeCustomerConversationThread({ ...thread, updatedAt: nowIso() });

  // Uten sky faller vi tilbake til den gamle plasseringen, slik at demo-/offline-modus
  // fortsatt virker.
  if (!conversationCloudReady()) {
    let updatedUiCustomer = null;
    await updateStateAndPersist((state) => {
      const customers = safeArray(state.customers);
      const index = customers.findIndex((entry) => entry.id === customerId);
      if (index < 0) throw new Error(t('svc.customerNotFound'));
      const nextThreads = [normalizedThread, ...safeArray(customers[index].conversationThreads).filter((entry) => entry?.id !== normalizedThread.id)];
      const nextCustomers = [...customers];
      nextCustomers[index] = stripUndefinedDeep({
        ...customers[index],
        nextFollowUpDate: normalizedThread.status === 'customer_waiting' ? todayIso() : customers[index].nextFollowUpDate,
        conversationThreads: nextThreads,
        conversationSummary: buildConversationSummary(nextThreads),
        updatedAt: nowIso(),
      });
      updatedUiCustomer = mapCustomerToUi(nextCustomers[index], { ...state, customers: nextCustomers });
      return { ...state, customers: nextCustomers };
    });
    window.dispatchEvent(new Event(BACKEND_EVENTS.customers));
    window.dispatchEvent(new Event(BACKEND_EVENTS.state));
    return updatedUiCustomer;
  }

  // Migrer forst hvis kunden fortsatt har traader liggende i app-state, ellers ville de
  // blitt usynlige i det vi begynner aa lese fra subcollectionen.
  const stateCustomer = safeArray(mergeState(runtime.appState).customers).find((entry) => entry.id === customerId);
  if (safeArray(stateCustomer?.conversationThreads).length) {
    await migrateConversationThreadsToCloud(customerId, stateCustomer.conversationThreads);
  }

  // Dette er hele skrivingen for en melding: ett lite dokument.
  await conversationsCollection(customerId).doc(normalizedThread.id).set(stripUndefinedDeep({
    ...normalizedThread,
    updatedByUserId: runtime.auth.currentUser.uid,
    updatedBySource: 'client',
  }), { merge: true });

  const threads = await loadCustomerConversationThreads(customerId);
  await syncConversationSummary(customerId, threads);

  const state = mergeState(runtime.appState);
  const nextCustomer = safeArray(state.customers).find((entry) => entry.id === customerId);
  if (!nextCustomer) throw new Error(t('svc.customerNotFound'));
  window.dispatchEvent(new Event(BACKEND_EVENTS.customers));
  return { ...mapCustomerToUi(nextCustomer, state), conversationThreads: threads };
};

const sendCustomerConversationEmail = async ({ customerId, threadId, messageText, subject } = {}) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));
  const result = await authorizedPost('sendCustomerConversationEmailHttp', {
    businessId: runtime.activeBusinessId,
    customerId,
    threadId,
    messageText,
    subject,
  });
  if (result.customer) {
    replaceCustomerLocal(result.customer);
    return {
      ...result,
      customer: mapCustomerToUi(result.customer, mergeState(runtime.appState)),
    };
  }
  return result;
};

const createOfferFromCustomerConversation = async (customerId, threadId) => {
  const state = mergeState(runtime.appState);
  const customer = safeArray(state.customers).find((entry) => entry.id === customerId);
  if (!customer) throw new Error(t('svc.customerNotFound'));
  const thread = (await loadCustomerConversationThreads(customerId)).find((entry) => entry.id === threadId);
  if (!thread) throw new Error(t('svc.conversationNotFound'));
  const quoteLines = safeArray(thread.quoteLines);
  if (!quoteLines.length) throw new Error(t('svc.addQuoteLine'));
  const uiCustomer = mapCustomerToUi(customer, state);
  const invoice = buildInvoiceFromEditor({
    docType: 'tilbud',
    customer: uiCustomer,
    invDate: todayIso(),
    dueDate: addDays(todayIso(), 30),
    reference: thread.title || '',
    descr: `Opprettet fra kundedialog: ${thread.title || 'Kundedialog'}`,
    lines: globalThis.PlummeCustomerConversationOffers.buildEditorLines({
      quoteLines,
      products: safeArray(state.products),
      createId,
    }),
  }, 'draft');
  const savedInvoice = await upsertInvoice(stripUndefinedDeep({
    ...invoice,
    customerId: customer.id,
    sourceCustomerConversation: {
      customerId: customer.id,
      threadId: thread.id,
    },
    sourceCustomerConversationThreadId: thread.id,
  }));
  const updatedCustomer = await saveCustomerConversationThread(customer.id, {
    ...thread,
    status: 'ready_for_offer',
    offerDraftInvoiceId: savedInvoice.id,
  });
  return {
    invoice: mapInvoiceToUi(savedInvoice, mergeState(runtime.appState)),
    customer: updatedCustomer,
  };
};
global.PlummeBackendCustomerConversations = {
  isLegacyDesignQuoteLine,
  normalizeCustomerConversationThread,
  createCustomerConversationThread,
  replaceCustomerLocal,
  conversationsCollection,
  conversationCloudReady,
  sortConversationThreads,
  buildConversationSummary,
  sameConversationSummary,
  syncConversationSummary,
  clearLegacyConversationThreads,
  migrateConversationThreadsToCloud,
  persistCleanedDesignFixtureLines,
  loadCustomerConversationThreads,
  moveCustomerConversationThreads,
  saveCustomerConversationThread,
  sendCustomerConversationEmail,
  createOfferFromCustomerConversation,
}; })(typeof window !== 'undefined' ? window : globalThis);
